import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  Paper,
  styled,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Contexts und Constants
import {
  Languages,
  TextModes,
  DarkModes,
  TextSizes,
} from "entities/constants";
import {
  TextSizeContext,
  DarkModeContext,
  BackgroundContext,
} from "data/index";

// Shared Components
import RadioButtons from "ui/components/shared/RadioButtons";
import ImageComponent from "ui/components/shared/ImageComponent";

// Assets
import Background1 from "assets/images/Background.png";
import Background2 from "assets/images/Background_Chat.png";

// === Mobile Header Component ===
const MobileHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: '0.75rem 1rem',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '2rem',
  gap: '1rem',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  position: 'sticky',
  top: 0,
  zIndex: 100,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  flex: 1,
}));

// === Mobile Container ===
const MobileContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  paddingBottom: '2rem',
}));

// === Content Wrapper ===
const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: '1rem',
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
}));

// === Mobile Cards ===
const MobileCard = styled(Paper)(({ theme }) => ({
  padding: "1.5rem",
  borderRadius: "12px",
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
  backgroundColor: theme.palette.common.white,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: "0.75rem",
}));

// === Background Grid (Mobile) ===
const BackgroundGrid = styled(Box)(() => ({
  display: "flex",
  gap: "0.75rem",
  flexWrap: "wrap",
  opacity: 0.5,
  pointerEvents: "none",
}));

const BackgroundTile = styled(Box)<{ selected?: boolean }>(({ theme, selected }) => ({
  width: "4rem",
  height: "4rem",
  borderRadius: "0.5rem",
  overflow: "hidden",
  border: selected ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
  boxShadow: theme.shadows[1],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

// === Disabled Section Wrapper ===
const DisabledSection = styled(Box)(() => ({
  opacity: 0.5,
  pointerEvents: "none",
}));

// === Component ===
const MobileLearningSettings = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const { state: textSize, updateContext: setTextSize } = useContext(TextSizeContext);
  const { state: darkMode } = useContext(DarkModeContext);
  const { state: backgroundImage } = useContext(BackgroundContext);

  const [textMode, setTextMode] = useState<number>(1);
  const [language, setLanguage] = useState(i18n.language === "de" ? 0 : 1);

  const handleTextModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextMode(Number(e.target.value));
  };

  const handleTextSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) as 0 | 1 | 2;
    setTextSize(value);
    localStorage.setItem("textSize", value.toString());
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const langCode = value === "0" ? "de" : "en";
    setLanguage(Number(value));
    i18n.changeLanguage(langCode);
    localStorage.setItem("language", langCode);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <MobileContainer>
      {/* Mobile Header */}
      <MobileHeader>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <HeaderTitle>{t("Learning Preferences")}</HeaderTitle>
      </MobileHeader>

      {/* Content */}
      <ContentWrapper>
        {/* Sprache - Separates Card */}
        <MobileCard>
          <SectionTitle>{t("Language")}</SectionTitle>
          <RadioButtons
            options={Languages.map((lang) => t(lang))}
            value={language}
            onChange={handleLanguageChange}
          />
        </MobileCard>

        {/* Textmodus */}
        <MobileCard>
          <SectionTitle>{t("Text Mode")}</SectionTitle>
          <RadioButtons
            options={TextModes.map((m) => t(m))}
            value={textMode}
            onChange={handleTextModeChange}
          />
        </MobileCard>

        {/* Textgröße */}
        <MobileCard>
          <SectionTitle>{t("Text Size")}</SectionTitle>
          <RadioButtons
            options={[t("Small"), t("Medium"), t("Large")]}
            value={textSize}
            onChange={handleTextSizeChange}
          />
        </MobileCard>

        {/* Dunkler Modus - Disabled */}
        <MobileCard>
          <DisabledSection>
            <SectionTitle>{t("Dark Mode")}</SectionTitle>
            <RadioButtons
              options={DarkModes.map((mode) => t(mode))}
              value={darkMode ? 0 : 1}
            />
          </DisabledSection>
        </MobileCard>

        {/* Hintergrund - Disabled */}
        <MobileCard>
          <DisabledSection>
            <SectionTitle>{t("Background")}</SectionTitle>
            <BackgroundGrid>
              {[Background1, Background2].map((img, index) => (
                <BackgroundTile key={index} selected={backgroundImage === index + 1}>
                  <ImageComponent
                    src={img}
                    alt={`background-${index + 1}`}
                    width="100%"
                    height="100%"
                    transform="scale(3)"
                  />
                </BackgroundTile>
              ))}
            </BackgroundGrid>
          </DisabledSection>
        </MobileCard>
      </ContentWrapper>
    </MobileContainer>
  );
};

export default MobileLearningSettings;